import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@xxtract/ui';
import { useAuthStore } from '@/stores/authStore';
import { useProfile } from '../hooks/useProfile';

function ReadOnlyField({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs font-medium text-[var(--foreground-muted)]">{label}</span>
      <p className="text-sm text-[var(--foreground)]" data-testid="profile-field-value">{value}</p>
    </div>
  );
}

/**
 * Strictly read-only (FR57): the profile never becomes editable here — changes go through
 * the administrator, per the adminContact hint below.
 */
export function ProfileCard() {
  const { t } = useTranslation();
  const user = useAuthStore((state) => state.user);
  const { data } = useProfile();

  const gln = user?.glnNumbers?.[0] ?? null;
  const companyName = data?.data.companyName ?? null;
  const companyDisplay = gln == null
    ? t('common.notAvailable')
    : companyName != null
      ? t('account.profile.companyWithGln', { company: companyName, gln })
      : t('account.profile.companyGlnOnly', { gln });

  return (
    <Card data-testid="profile-card">
      <CardHeader>
        <CardTitle>{t('account.profile.title')}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <ReadOnlyField label={t('account.profile.name')} value={user?.name ?? t('common.notAvailable')} />
        <ReadOnlyField label={t('account.profile.email')} value={user?.email ?? t('common.notAvailable')} />
        <ReadOnlyField label={t('account.profile.company')} value={companyDisplay} />
        <p className="text-xs text-[var(--foreground-muted)]">{t('account.profile.adminContact')}</p>
      </CardContent>
    </Card>
  );
}
